import { Outlet } from 'react-router-dom'

import { TimeoffPolicyRootPageDocument } from 'generated/resources/graphql'

import type { Back, Tab } from 'modules/core/components/Card/types'
import Loading from 'modules/core/components/Loading'
import NotFound from 'modules/core/components/NotFound'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import i18n from 'modules/core/lib/i18n'

import { useModule as useTimeoffModule } from 'modules/timeoff/hooks/useModule'
import type { RouteContextParams } from 'modules/timeoff/lib/routeContextParams'
import timeoffPolicies from 'modules/timeoff/store/collections/timeoffPolicies/policies'

const TimeoffPolicyRootPage = () => {
  const { navigation } = useNavigation()
  const { id } = useStrictParams<'id'>()

  const back: Back = {
    href: navigation.settings.timeoffPolicies.fullPath(),
    label: i18n.t('settings.timeoff_policies.tab'),
  }

  const tabs = useMemo<Tab[]>(
    () => [
      {
        name: i18n.t('settings.timeoff_policies.navigation.basic_info'),
        index: true,
        href: navigation.settings.timeoffPolicies.id.details.fullPath({ id }),
      },
      {
        name: i18n.t('settings.timeoff_policies.navigation.employees'),
        index: true,
        href: navigation.settings.timeoffPolicies.id.employees.fullPath({ id }),
      },
    ],
    [id]
  )

  const { loading: loadingTimeoff, enabled: timeoffEnabled } = useTimeoffModule()
  const { loading: loadingPolicies } = useAsyncData(async () => {
    if (timeoffPolicies.models.length === 0) {
      await timeoffPolicies.fetch()
    }
  }, [loadingTimeoff])
  const { loading: loadingGraphQl, data } = useQuery(TimeoffPolicyRootPageDocument)

  if (loadingTimeoff || loadingPolicies || loadingGraphQl) return <Loading />

  if (!timeoffEnabled) return <NotFound />

  const policy = timeoffPolicies.get(Number(id))
  const isNotFound = !policy
  const title = policy?.attributes.name
  const canManageTimeoff = data?.timeoff?._policies.companyManageTimeoff.can ?? false

  const values: RouteContextParams = {
    isNotFound,
    back,
    tabs,
    policy,
    blockedPeriodsPolicy: undefined,
    loading: false,
    title,
  }

  if (!canManageTimeoff) return <NotFound />

  return <Outlet context={values} />
}

export default TimeoffPolicyRootPage
